import React, { useState, useEffect, useCallback, useRef } from "react"
import { Drawer, Tooltip, Switch, ColorPicker } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronLeft,
  faChevronRight,
  faLayerGroup,
  faObjectGroup,
  faExpand,
  faCompress,
  faPalette,
} from "@fortawesome/free-solid-svg-icons"
import ReportingCanvas from "./ReportingCanvas.component"
import ReportingElementTree from "./ReportingElementTree.component"
import SlideManager from "./SlideManager.component"
import TemplateSelector from "./TemplateSelector.component"
import TemplateModal from "./TemplateModal.component"
import { Loader } from "shared/ui/Loader/Loader.component"

import { useResponsive } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useResponsive"
import { useUIState } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useUIState"
import { useSlideManagement } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useSlideManagement"
import { useMergedSlideContent } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useMergedSlideContent"
import { useElementManagement } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useElementManagement"
import { useTemplateManagement } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useTemplateManagement"
import { useBackgroundColorManagement } from "features/Reporting/hooks/Dashboard/ReportingDisplay/useBackgroundColorManagement"
import useSequentialExecution from "features/Reporting/hooks/DataLoading/useSequentialExecution"

// Types
import {
  DashboardConfig,
  DashboardConfigChange,
  SlideForExport,
  TableDataCache,
  Slide,
} from "types/ReportingDashboard/reporting.types"
import { EngineQueryResult } from "types/engineOutput.types"
import {
  SlideElement,
  DataElementConfig,
  ELEMENT_TYPE,
  TableElementSettings,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"

/**
 * Props for the ReportingDisplay component
 */
export interface ReportingDisplayProps {
  /** Current dashboard configuration with slides and templates */
  dashboardConfig: DashboardConfig
  /** Callback function to update dashboard configuration */
  onDashboardConfigChange: (change: DashboardConfigChange) => void

  /** Callback to update slides prepared for PPTX export */
  onSlidesForExportUpdate: (slides: SlideForExport[]) => void
  /** ID of the currently active slide (controlled by parent) */
  activeSlideId: string | null
  /** Function to update the active slide ID in parent */
  setActiveSlideId: (slideId: string | null) => void
  /** Data codebook for element configuration */
  codebook: CodebookData
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Current filter configuration */
  filter: string

  /** Runtime cache for table data */
  tableDataCache: TableDataCache
  /** Callback to update table data cache */
  onTableDataUpdate: (
    elementId: string,
    tableData: EngineQueryResult | null
  ) => void
}

/**
 * Element ref with execution methods
 */
export interface ElementRef {
  hasDataAndShouldRefresh?: () => boolean
  requestSequentialExecution?: (reason: string) => void
}

/**
 * Main container component for the dashboard reporting interface.
 *
 * Provides a comprehensive dashboard creation and editing experience with:
 * - Responsive design with mobile-first approach
 * - Template system for reusable slide layouts
 * - Slide management (create, delete, duplicate, reorder)
 * - Element management with drag-and-drop canvas
 * - Sequential execution system for all data elements
 * - Fullscreen mode for focused editing
 * - PPTX export preparation
 * - Background color customization for slides and templates
 *
 * The component uses a controlled pattern where the active slide ID is managed by the parent,
 * allowing for external navigation control while maintaining internal state consistency.
 */
const ReportingDisplay: React.FC<ReportingDisplayProps> = ({
  dashboardConfig,
  onDashboardConfigChange,
  onSlidesForExportUpdate,
  activeSlideId: activeSlideIdFromParent,
  setActiveSlideId: setActiveSlideIdInParent,
  codebook,
  queryInstance,
  filter,
  tableDataCache,
  onTableDataUpdate,
}) => {
  // ========================================================================================
  // HOOKS AND STATE MANAGEMENT
  // ========================================================================================
  const { isSmallScreen, isMobileScreen } = useResponsive()
  const {
    leftSidebarOpen,
    rightSidebarOpen,
    elementsDrawerVisible,
    slidesDrawerVisible,
    isFullScreen,
    containerRef,
    toggleFullScreen,
    toggleLeftSidebar,
    toggleRightSidebar,
    toggleElementsDrawer,
    toggleSlidesDrawer,
  } = useUIState(isSmallScreen)

  // Grid visibility state
  const [showGrid, setShowGrid] = useState(true)

  // Controlled slide ID from parent
  const activeSlideId = activeSlideIdFromParent
  const setActiveSlideId = setActiveSlideIdInParent

  // ========================================================================================
  // SEQUENTIAL EXECUTION SYSTEM FOR ALL DATA ELEMENTS
  // ========================================================================================

  // Sequential execution system to prevent concurrent element script runs
  const { requestExecution, isExecuting } = useSequentialExecution({
    delayBetweenExecutions: 300,
    onError: (elementId, error, reason) => {
      console.error(`Error executing element ${elementId} (${reason}):`, error)
    },
  })
  const elementRefsMap = useRef<Map<string, ElementRef>>(new Map()) // Track all data element refs

  /**
   * Handler for element execution requests
   * All data elements call this instead of executing directly
   */
  const handleElementExecutionRequest = useCallback(
    (
      elementId: string,
      executeFunction: () => Promise<void>,
      reason: string = "user_initiated"
    ): void => {
      requestExecution(elementId, executeFunction, reason)
    },
    [requestExecution]
  )

  /**
   * Register a data element ref for potential bulk operations
   */
  const registerElementRef = useCallback(
    (elementId: string, ref: ElementRef | null): void => {
      if (ref) {
        elementRefsMap.current.set(elementId, ref)
      } else {
        elementRefsMap.current.delete(elementId)
      }
    },
    []
  )

  // ========================================================================================
  // SLIDE MANAGEMENT HOOK (Initial - will be updated after template hook)
  // ========================================================================================

  // First call to get basic slide data
  const slideManagementInitial = useSlideManagement({
    dashboardConfig,
    onDashboardConfigChange,
    activeSlideId,
    setActiveSlideId,
    activeTemplate: null, // Will be updated after template hook
    isEditingTemplate: false, // Will be updated after template hook
    editingTemplate: null, // Will be updated after template hook
    originalTemplateState: null, // Managed by template hook
  })

  const { currentSlides, currentTemplates, activeSlide, isSwitchingSlides } =
    slideManagementInitial

  // ========================================================================================
  // BACKGROUND COLOR MANAGEMENT HOOK (Initial)
  // ========================================================================================

  const { getCurrentBackgroundColor } = useBackgroundColorManagement({
    activeSlide,
    activeSlideId,
    currentSlides,
    currentTemplates,
    dashboardConfig,
    isEditingTemplate: false, // Will be updated after template hook
    editingTemplate: null, // Will be updated after template hook
    onDashboardConfigChange,
  })

  /**
   * Gets elements to save when creating/updating a template
   */
  const getElementsToSave = useCallback((): SlideElement[] => {
    if (activeSlide) {
      const baseTemplateId = activeSlide.templateId
      const baseTemplateElements = baseTemplateId
        ? JSON.parse(
            JSON.stringify(currentTemplates[baseTemplateId]?.elements || [])
          )
        : []
      const slideElements = activeSlide.slideElements || []
      return [...baseTemplateElements, ...slideElements]
    }
    return []
  }, [activeSlide, currentTemplates])

  /**
   * Gets table configurations to save when creating/updating a template
   */
  const getTableConfigsToSave = useCallback((): Record<
    string,
    DataElementConfig
  > => {
    if (activeSlide) {
      const baseTemplateId = activeSlide.templateId
      const baseTemplateConfigs = baseTemplateId
        ? currentTemplates[baseTemplateId]?.tableConfigs || {}
        : {}
      const slideConfigs = activeSlide.slideTableConfigs || {}
      return { ...baseTemplateConfigs, ...slideConfigs }
    }
    return {}
  }, [activeSlide, currentTemplates])

  // ========================================================================================
  // TEMPLATE MANAGEMENT HOOK
  // ========================================================================================

  const {
    activeTemplate,
    isEditingTemplate,
    templateModalVisible,
    templateModalMode,
    editingTemplate,
    templateBeingEditedName,
    selectTemplate,
    deleteTemplate,
    openEditTemplateModal,
    openSaveTemplateModal,
    handleTemplateModalCancel,
    handleTemplateModalSave,
  } = useTemplateManagement({
    currentSlides,
    currentTemplates,
    activeSlideId,
    activeSlide: activeSlide || null,
    onDashboardConfigChange,
    getCurrentBackgroundColor,
    getElementsToSave,
    getTableConfigsToSave,
  })

  // ========================================================================================
  // BACKGROUND COLOR MANAGEMENT HOOK (Complete - with template context)
  // ========================================================================================

  const {
    handleBackgroundColorChange,
    getCurrentBackgroundColor: getCurrentBgColor,
  } = useBackgroundColorManagement({
    activeSlide,
    activeSlideId,
    currentSlides,
    currentTemplates,
    dashboardConfig,
    isEditingTemplate,
    editingTemplate,
    onDashboardConfigChange,
  })

  // ========================================================================================
  // SLIDE MANAGEMENT HOOK (Complete - with template context)
  // ========================================================================================

  const {
    safeSetActiveSlide,
    handleSlideAdd,
    handleSlideDelete,
    handleSlideDuplicate,
  } = useSlideManagement({
    dashboardConfig,
    onDashboardConfigChange,
    activeSlideId,
    setActiveSlideId,
    activeTemplate,
    isEditingTemplate,
    editingTemplate,
    originalTemplateState: null, // Managed by template hook
  })

  // ========================================================================================
  // CONTENT MERGING HOOK
  // ========================================================================================

  const { mergedSlideContent, getMergedSlideForDisplay } =
    useMergedSlideContent({
      activeSlide,
      currentTemplates,
      isEditingTemplate,
      editingTemplate,
    })

  /**
   * Process all data elements sequentially on filter changes
   */
  const processAllElementsOnFilterChange =
    useCallback(async (): Promise<void> => {
      if (isExecuting()) return

      // Find all data elements that need to be re-run due to filter change
      const allElements = mergedSlideContent.elements || []
      const dataElements = allElements.filter((el: SlideElement) =>
        [
          ELEMENT_TYPE.TABLE,
          ELEMENT_TYPE.CHART,
          ELEMENT_TYPE.PIE_CHART,
          ELEMENT_TYPE.TIME_SERIES_CHART,
        ].includes(el.type)
      )

      for (const element of dataElements) {
        const elementRef = elementRefsMap.current.get(element.id)
        if (
          elementRef &&
          typeof elementRef.hasDataAndShouldRefresh === "function"
        ) {
          const shouldRefresh = elementRef.hasDataAndShouldRefresh()
          if (
            shouldRefresh &&
            typeof elementRef.requestSequentialExecution === "function"
          ) {
            elementRef.requestSequentialExecution("filter_change")
          }
        }
      }
    }, [mergedSlideContent, isExecuting])

  // Monitor filter changes and trigger sequential element updates
  const prevFilterRef = useRef<string>(filter)
  useEffect(() => {
    if (filter !== prevFilterRef.current) {
      processAllElementsOnFilterChange()
      prevFilterRef.current = filter
    }
  }, [filter, processAllElementsOnFilterChange])

  // ========================================================================================
  // ELEMENT MANAGEMENT HOOK
  // ========================================================================================

  const { handleElementsChange, handleTableConfigUpdate, handleElementDelete } =
    useElementManagement({
      activeSlide,
      activeSlideId,
      isEditingTemplate,
      editingTemplate,
      currentSlides,
      currentTemplates,
      onDashboardConfigChange,
      mergedSlideContent,
    })

  // ========================================================================================
  // EXPORT PREPARATION EFFECT
  // ========================================================================================

  /**
   * Notifies parent component when slides change for PPTX export
   * Enhances slides with cached table data for complete export preparation
   */
  useEffect(() => {
    const convertedSlidesForExport: SlideForExport[] = currentSlides.map(
      (slide: Slide) => {
        const merged = getMergedSlideForDisplay(slide)
        const enhancedTableConfigs = { ...merged.tableConfigs }

        Object.keys(enhancedTableConfigs).forEach((elementId) => {
          if (tableDataCache && tableDataCache[elementId]) {
            const config = enhancedTableConfigs[elementId]

            // Type guard: only add tableData to TableElementSettings
            if (config && "crossTabVariables" in config) {
              enhancedTableConfigs[elementId] = {
                ...config,
                tableData: tableDataCache[elementId],
              } as TableElementSettings
            }
          }
        })

        // Determine background color for export
        let slideBackgroundColor = slide.backgroundColor
        if (
          !slideBackgroundColor &&
          slide.templateId &&
          currentTemplates[slide.templateId]
        ) {
          slideBackgroundColor =
            currentTemplates[slide.templateId].backgroundColor
        }
        if (!slideBackgroundColor) {
          slideBackgroundColor =
            dashboardConfig.defaultBackgroundColor || "#ffffff"
        }

        return {
          id: slide.id,
          elements: merged.elements,
          tableConfigs: enhancedTableConfigs,
          backgroundColor: slideBackgroundColor,
        }
      }
    )
    onSlidesForExportUpdate(convertedSlidesForExport)
  }, [
    currentSlides,
    currentTemplates,
    dashboardConfig.defaultBackgroundColor,
    getMergedSlideForDisplay,
    onSlidesForExportUpdate,
    tableDataCache,
  ])

  // ========================================================================================
  // RENDER
  // ========================================================================================

  return (
    <div
      ref={containerRef}
      className="flex h-full w-full bg-white rounded-md relative"
    >
      {/* Mobile Drawers */}
      <Drawer
        title="Elements"
        placement="left"
        open={elementsDrawerVisible}
        onClose={toggleElementsDrawer}
        width={isMobileScreen ? "85%" : 256}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ padding: "8px 16px" }}
        mask={false}
        maskClosable={false}
      >
        <ReportingElementTree />
      </Drawer>
      <Drawer
        title="Pages & Templates"
        placement="right"
        open={slidesDrawerVisible}
        onClose={toggleSlidesDrawer}
        width={isMobileScreen ? "85%" : 256}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ padding: "8px 16px" }}
        mask={false}
        maskClosable={false}
      >
        <div className="flex flex-col h-full w-full">
          <TemplateSelector
            templates={currentTemplates}
            activeTemplate={activeTemplate}
            onSelectTemplate={selectTemplate}
            onSaveTemplate={openSaveTemplateModal}
            onEditTemplate={openEditTemplateModal}
            onDeleteTemplate={deleteTemplate}
            isEditingTemplate={isEditingTemplate}
            editingTemplate={editingTemplate}
            activeSlideTemplateId={activeSlide?.templateId}
          />
          <SlideManager
            slides={currentSlides}
            activeSlideId={activeSlideId}
            onSlideChange={safeSetActiveSlide}
            onSlideAdd={handleSlideAdd}
            onSlideDelete={handleSlideDelete}
            onSlideDuplicate={handleSlideDuplicate}
            hasTemplate={activeTemplate !== null}
            isTemplateMode={isEditingTemplate}
            getTemplateNameForSlide={(slide: Slide) =>
              slide.templateId
                ? currentTemplates[slide.templateId]?.name || null
                : null
            }
          />
        </div>
      </Drawer>

      {/* Mobile Header */}
      {isSmallScreen && (
        <div className="absolute top-0 left-0 right-0 z-40 bg-white shadow-md flex justify-between items-center px-3 py-3 rounded-lg">
          {!isFullScreen && (
            <div className="flex items-center gap-3">
              <FontAwesomeIcon
                icon={faObjectGroup}
                onClick={toggleElementsDrawer}
                color="#36c3ed"
                className="cursor-pointer hover:text-blue-500 transition-colors"
              />
              <FontAwesomeIcon
                icon={faLayerGroup}
                onClick={toggleSlidesDrawer}
                color="#36c3ed"
                className="cursor-pointer hover:text-blue-500 transition-colors"
              />
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">Grid</span>
              <Switch
                size="small"
                checked={showGrid}
                onChange={setShowGrid}
                title="Toggle Grid Lines"
                style={{ backgroundColor: "#36c3ed" }}
              />
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Background Color">
                <ColorPicker
                  value={getCurrentBgColor()}
                  onChange={(color) =>
                    handleBackgroundColorChange(color.toHexString())
                  }
                  size="small"
                  showText={false}
                  trigger="click"
                >
                  <FontAwesomeIcon
                    icon={faPalette}
                    color="#36c3ed"
                    className="cursor-pointer hover:text-blue-500 transition-colors"
                  />
                </ColorPicker>
              </Tooltip>
            </div>

            <FontAwesomeIcon
              icon={isFullScreen ? faCompress : faExpand}
              color="#36c3ed"
              onClick={toggleFullScreen}
              className="cursor-pointer hover:text-blue-500 transition-colors"
            />
          </div>
        </div>
      )}

      {/* Desktop Sidebar Controls */}
      {!isSmallScreen && (
        <>
          <div
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-r-md cursor-pointer"
            onClick={toggleLeftSidebar}
          >
            <div className="p-2 text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon
                icon={leftSidebarOpen ? faChevronLeft : faChevronRight}
              />
            </div>
          </div>

          <div
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-l-md cursor-pointer"
            onClick={toggleRightSidebar}
          >
            <div className="p-2 text-gray-500 hover:text-gray-700">
              <FontAwesomeIcon
                icon={rightSidebarOpen ? faChevronRight : faChevronLeft}
              />
            </div>
          </div>
        </>
      )}

      {/* Left Sidebar */}
      {!isSmallScreen && (
        <div
          className={`transition-all duration-300 ${
            leftSidebarOpen
              ? "w-64 opacity-100"
              : "w-0 opacity-0 overflow-hidden"
          }`}
        >
          <ReportingElementTree />
        </div>
      )}

      {/* Desktop Header */}
      {!isSmallScreen && (
        <div
          className={`absolute top-0 z-40 flex items-center gap-3 bg-white/90 backdrop-blur-sm shadow-md rounded-lg px-3 py-3  ${
            leftSidebarOpen ? "left-64" : "left-5"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Grid:</span>
            <Switch
              size="small"
              checked={showGrid}
              onChange={setShowGrid}
              title="Toggle Grid Lines"
              style={{ backgroundColor: showGrid ? "#36c3ed" : "#d9d9d9" }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Background:</span>
            <Tooltip title="Background Color">
              <ColorPicker
                value={getCurrentBgColor()}
                onChange={(color) =>
                  handleBackgroundColorChange(color.toHexString())
                }
                size="small"
                showText={false}
                trigger="click"
              >
                <FontAwesomeIcon
                  icon={faPalette}
                  color="#36c3ed"
                  className="cursor-pointer hover:text-blue-500 transition-colors"
                />
              </ColorPicker>
            </Tooltip>
          </div>
          <span className="text-xs text-gray-600">Fullscreen:</span>

          <Tooltip
            title={isFullScreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            <FontAwesomeIcon
              icon={isFullScreen ? faCompress : faExpand}
              color="#36c3ed"
              onClick={toggleFullScreen}
              className="cursor-pointer hover:text-blue-500 transition-colors"
            />
          </Tooltip>
        </div>
      )}

      {/* Main Canvas Area */}
      <div className="flex-grow overflow-hidden flex justify-center items-center">
        <div
          style={{
            width:
              !rightSidebarOpen && !leftSidebarOpen
                ? "80%"
                : !rightSidebarOpen || !leftSidebarOpen
                ? "85%"
                : "100%",
            height: "100%",
            maxWidth: "calc(100vw - 50px)",
            maxHeight: "calc(100vh - 230px)",
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%", height: "100%", position: "relative" }}>
            {isSwitchingSlides && (
              <div className="absolute inset-0 bg-white flex items-center justify-center z-50 rounded-lg">
                <Loader fontSize={48} color="#36C3ED" />
              </div>
            )}
            {activeSlide ? (
              <ReportingCanvas
                key={
                  isEditingTemplate
                    ? `template_${editingTemplate}`
                    : `slide_${activeSlideId}`
                }
                codebook={codebook}
                filter={filter}
                elements={mergedSlideContent.elements || []}
                onElementsChange={handleElementsChange}
                queryInstance={queryInstance}
                tableConfigs={mergedSlideContent.tableConfigs || {}}
                onTableConfigUpdate={handleTableConfigUpdate}
                isTemplateMode={isEditingTemplate}
                templateName={templateBeingEditedName}
                onElementDelete={handleElementDelete}
                isSmallScreen={isSmallScreen}
                onTableDataUpdate={onTableDataUpdate}
                onElementExecutionRequest={handleElementExecutionRequest}
                onRegisterElementRef={registerElementRef}
                showGrid={showGrid}
                backgroundColor={getCurrentBgColor()}
              />
            ) : currentSlides.length > 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">Loading slide...</p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-gray-500">
                  No slides available. Add a slide to begin.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      {!isSmallScreen && (
        <div
          className={`transition-all duration-300 ${
            rightSidebarOpen
              ? "w-64 opacity-100"
              : "w-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="flex flex-col h-full border-l border-gray-200">
            <TemplateSelector
              templates={currentTemplates}
              activeTemplate={activeTemplate}
              onSelectTemplate={selectTemplate}
              onSaveTemplate={openSaveTemplateModal}
              onEditTemplate={openEditTemplateModal}
              onDeleteTemplate={deleteTemplate}
              isEditingTemplate={isEditingTemplate}
              editingTemplate={editingTemplate}
              activeSlideTemplateId={activeSlide?.templateId}
            />
            <SlideManager
              slides={currentSlides}
              activeSlideId={activeSlideId}
              onSlideChange={safeSetActiveSlide}
              onSlideAdd={handleSlideAdd}
              onSlideDelete={handleSlideDelete}
              onSlideDuplicate={handleSlideDuplicate}
              hasTemplate={activeTemplate !== null}
              isTemplateMode={isEditingTemplate}
              getTemplateNameForSlide={(slide: Slide) =>
                slide.templateId
                  ? currentTemplates[slide.templateId]?.name
                  : null
              }
            />
          </div>
        </div>
      )}

      {/* Template Modal */}
      <TemplateModal
        isVisible={templateModalVisible}
        onCancel={handleTemplateModalCancel}
        onSave={handleTemplateModalSave}
        existingTemplates={currentTemplates}
        mode={templateModalMode}
        selectedTemplate={
          templateModalMode === "edit" && editingTemplate
            ? editingTemplate
            : null
        }
        title={
          templateModalMode === "create"
            ? "Save as New Template"
            : "Save Template Changes"
        }
      />
    </div>
  )
}

export default ReportingDisplay
